import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, TextField, Button, FormControl, InputLabel,
    Select, MenuItem, IconButton, Chip, Dialog, DialogTitle,
    DialogContent, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OrgTree from 'react-org-tree';
import axios from 'axios';
import * as common from '../../../commons/common';

// ────────────────────────────────────────────────────────────────
// 매니저 OrgChartDialog와 동일한 테마
// ────────────────────────────────────────────────────────────────
const TIER_CONFIG = [
    { accent: '#3b82f6', label: '총괄부서' },
    { accent: '#06b6d4', label: '관리부서' },
    { accent: '#10b981', label: '팀' },
];

const NodeCard = ({ label, depth, onClick }) => {
    if (depth < 0) {
        return (
            <Box sx={{
                backgroundColor: '#1e293b',
                borderRadius: '8px',
                width: 120,
                py: 0.75,
                boxShadow: '0 2px 8px rgba(15,23,42,0.20)',
                textAlign: 'center',
            }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                    {label}
                </Typography>
            </Box>
        );
    }
    const cfg = TIER_CONFIG[depth] || TIER_CONFIG[TIER_CONFIG.length - 1];
    return (
        <Box
            onClick={onClick}
            sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e8edf2',
                width: 120,
                py: 0.75,
                px: 1.2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.10)', borderColor: '#93c5fd' },
            }}
        >
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: cfg.accent, flexShrink: 0 }} />
            <Typography sx={{
                fontSize: '0.76rem', fontWeight: 600,
                color: '#1e293b', lineHeight: 1.3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}>
                {label}
            </Typography>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 조직관리 팝업 (편집 기능 포함)
// ────────────────────────────────────────────────────────────────
const OrgManagementDialog = ({ open, onClose }) => {
    const [showGeneralDepartmentSelect, setShowGeneralDepartmentSelect] = useState(false);
    const [showManagementDepartmentSelect, setShowManagementDepartmentSelect] = useState(false);
    const [showNewDepartmentInput, setShowNewDepartmentInput] = useState(false);
    const [newDepartment, setNewDepartment] = useState('');
    const [departmentType, setDepartmentType] = useState('');
    const [selectedGeneralDepartment, setSelectedGeneralDepartment] = useState('');
    const [selectedManagementDepartment, setSelectedManagementDepartment] = useState('');
    const [organizationData, setOrganizationData] = useState(null);
    const [generalDepartments, setGeneralDepartments] = useState([]);
    const [managementDepartments, setManagementDepartments] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editDepartmentId, setEditDepartmentId] = useState(null);
    const [editDepartmentName, setEditDepartmentName] = useState('');
    const [treeScale, setTreeScale] = useState(1);

    const containerRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTreeScale(1);
            setOrganizationData(null);
            resetForm();
            setEditMode(false);
            fetchOrganizationData();
        }
    }, [open]);

    // zoom-to-fit
    useEffect(() => {
        if (!organizationData) return;
        const timer = setTimeout(() => {
            const container = containerRef.current;
            const inner = innerRef.current;
            if (!container || !inner) return;
            const available = container.clientWidth - 32;
            const natural = inner.scrollWidth;
            if (natural > available) {
                setTreeScale(available / natural);
            } else {
                setTreeScale(1);
            }
        }, 120);
        return () => clearTimeout(timer);
    }, [organizationData]);

    useEffect(() => {
        if (departmentType === 'Tier2' || departmentType === 'Tier3') {
            fetchDepartments('TOTAL', 0);
        }
    }, [departmentType]);

    useEffect(() => {
        if (selectedGeneralDepartment && (departmentType === 'Tier2' || departmentType === 'Tier3')) {
            fetchDepartments('MANAGE', selectedGeneralDepartment);
        }
    }, [selectedGeneralDepartment]);

    const fetchDepartments = async (deptType, upperDeptId = 0) => {
        try {
            const response = await axios.get(`${common.getApiUrl()}/dept`, {
                params: { upperDeptId, deptType },
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            });
            if (Array.isArray(response.data)) {
                if (deptType === 'TOTAL') setGeneralDepartments(response.data);
                else if (deptType === 'MANAGE') setManagementDepartments(response.data);
            }
        } catch (error) {
            console.error(`Error fetching ${deptType} departments:`, error);
        }
    };

    const fetchOrganizationData = async () => {
        const token = localStorage.getItem('access_token');
        try {
            const response = await axios.get(`${common.getApiUrl()}/dept/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOrganizationData(buildTree(response.data));
        } catch (error) {
            console.error('조직 데이터를 가져오는데 실패했습니다:', error);
            alert('조직 데이터를 가져올 수 없습니다.');
        }
    };

    const buildTree = (data) => {
        const map = {};
        data.forEach(dept => { map[dept.deptId] = { ...dept, children: [] }; });
        data.forEach(dept => {
            if (dept.upperDept && dept.upperDept !== 0 && map[dept.upperDept]) {
                map[dept.upperDept].children.push(map[dept.deptId]);
            }
        });
        const roots = data
            .filter(d => !d.upperDept || d.upperDept === 0)
            .map(d => map[d.deptId]);
        return {
            label: <NodeCard label="조직" depth={-1} />,
            children: roots.map(d => toTreeNode(d, 0)),
        };
    };

    const toTreeNode = (dept, depth) => ({
        label: (
            <NodeCard
                label={dept.deptNm}
                depth={depth}
                onClick={() => handleEditDepartment(dept.deptId, dept.deptNm)}
            />
        ),
        key: String(dept.deptId),
        children: (dept.children || []).map(child => toTreeNode(child, depth + 1)),
    });

    const handleDepartmentTypeChange = (event) => {
        const type = event.target.value;
        setDepartmentType(type);
        setSelectedGeneralDepartment('');
        setSelectedManagementDepartment('');
        setNewDepartment('');
        setShowGeneralDepartmentSelect(type === 'Tier2' || type === 'Tier3');
        setShowManagementDepartmentSelect(type === 'Tier3');
        setShowNewDepartmentInput(type !== '');
    };

    const handleGeneralDepartmentChange = (event) => {
        const id = event.target.value;
        setSelectedGeneralDepartment(id);
        setSelectedManagementDepartment('');
        fetchDepartments('MANAGE', id);
    };

    const handleManagementDepartmentChange = (event) => {
        setSelectedManagementDepartment(event.target.value);
    };

    const handleAddDepartment = async () => {
        if (!newDepartment.trim()) { alert('부서명을 입력해주세요.'); return; }
        let upperDeptId = null;
        if (departmentType === 'Tier2') upperDeptId = selectedGeneralDepartment;
        if (departmentType === 'Tier3') upperDeptId = selectedManagementDepartment;

        const departmentData = {
            deptNm: newDepartment,
            upperDept: upperDeptId,
            deptType: departmentType === 'Tier1' ? 'TOTAL' : departmentType === 'Tier2' ? 'MANAGE' : 'TEAM',
        };

        try {
            const response = await axios.post(`${common.getApiUrl()}/dept`, departmentData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.status === 200) {
                alert('부서가 성공적으로 추가되었습니다.');
                resetForm();
                setTreeScale(1);
                await fetchOrganizationData();
            } else {
                alert('부서를 추가하지 못했습니다.');
            }
        } catch (error) {
            console.error('Error adding department:', error);
            alert('부서를 추가하는 데 실패했습니다.');
        }
    };

    const resetForm = () => {
        setNewDepartment('');
        setDepartmentType('');
        setSelectedGeneralDepartment('');
        setSelectedManagementDepartment('');
        setShowGeneralDepartmentSelect(false);
        setShowManagementDepartmentSelect(false);
        setShowNewDepartmentInput(false);
    };

    const handleEditDepartment = (deptId, deptNm) => {
        setEditMode(true);
        setEditDepartmentId(deptId);
        setEditDepartmentName(deptNm);
    };

    const handleUpdateDepartment = async () => {
        if (!editDepartmentName.trim()) { alert('부서명을 입력해주세요.'); return; }
        try {
            const response = await axios.put(
                `${common.getApiUrl()}/dept/${editDepartmentId}`,
                { deptId: editDepartmentId, deptNm: editDepartmentName },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (response.status === 200) {
                alert('부서가 성공적으로 수정되었습니다.');
                setEditMode(false);
                setTreeScale(1);
                await fetchOrganizationData();
            } else {
                alert('부서를 수정하지 못했습니다.');
            }
        } catch (error) {
            console.error('Error updating department:', error);
            alert('부서를 수정하는 데 실패했습니다.');
        }
    };

    const handleDeleteDepartment = async () => {
        if (!window.confirm('정말로 이 부서를 삭제하시겠습니까?')) return;
        try {
            const response = await axios.delete(
                `${common.getApiUrl()}/dept/${editDepartmentId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
            );
            if (response.status === 200) {
                alert('부서가 성공적으로 삭제되었습니다.');
                setEditMode(false);
                setTreeScale(1);
                await fetchOrganizationData();
            } else {
                alert('부서를 삭제하지 못했습니다.');
            }
        } catch (error) {
            console.error('Error deleting department:', error);
            alert('부서를 삭제하는 데 실패했습니다.');
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditDepartmentId(null);
        setEditDepartmentName('');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 12px 48px rgba(0,0,0,0.16)',
                    overflow: 'hidden',
                    width: '90vw',
                    maxWidth: 1100,
                    backgroundColor: '#fff',
                }
            }}
        >
            {/* 헤더 – OrgChartDialog와 동일한 스타일 */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: '1px solid #f1f5f9',
                }}>
                    <Box display="flex" alignItems="center" gap={1.2}>
                        <AccountTreeOutlinedIcon sx={{ color: '#1565C0', fontSize: '1.1rem' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                            조직 관리
                        </Typography>
                        {/* 범례 */}
                        <Box display="flex" gap={1.5} ml={0.5}>
                            {TIER_CONFIG.map((cfg, i) => (
                                <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: cfg.accent }} />
                                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500 }}>
                                        {cfg.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Chip
                            label="노드 클릭 수정"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '18px', '& .MuiChip-label': { px: 0.7 }, color: '#64748b', borderColor: '#e2e8f0' }}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* 바디: 상단 조직도 + 하단 폼 */}
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* 상단: 조직도 트리 – 전체 너비 사용, 컨텐츠 높이에 맞게 자동 축소 */}
                <Box
                    ref={containerRef}
                    sx={{
                        overflow: 'auto',
                        maxHeight: 'calc(80vh - 120px)',
                        px: 3, py: 2,
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        '& .org-tree-node-label .org-tree-node-label-inner': {
                            padding: '0 !important',
                            border: 'none !important',
                            boxShadow: 'none !important',
                            borderRadius: '0 !important',
                            background: 'transparent !important',
                        },
                        '& .org-tree-node-btn': { display: 'none !important' },
                        '& .org-tree': { backgroundColor: 'transparent !important' },
                        '& .org-tree-node::before': { borderTopColor: '#e2e8f0 !important' },
                        '& .org-tree-node::after': { borderLeftColor: '#e2e8f0 !important' },
                        '& .org-tree-node-children::before': { borderLeftColor: '#e2e8f0 !important' },
                    }}
                >
                    {organizationData ? (
                        <Box ref={innerRef} style={{ zoom: treeScale, transition: 'zoom 0.2s ease' }}>
                            <OrgTree data={organizationData} horizontal={false} collapsable={false} />
                        </Box>
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center"
                            sx={{ flex: 1, color: '#94a3b8', fontSize: '0.82rem', py: 6 }}>
                            데이터를 불러오는 중...
                        </Box>
                    )}
                </Box>

                {/* 하단: 등록/수정 폼 – 가로 한 줄 배치 */}
                <Box sx={{
                    flexShrink: 0,
                    borderTop: '1px solid #f1f5f9',
                    backgroundColor: '#fafbfc',
                    px: 2.5, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minHeight: 72,
                }}>
                    {!editMode ? (
                        <>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
                                부서 등록
                            </Typography>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

                            {/* Tier 선택 */}
                            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                                <InputLabel sx={{ fontSize: '0.78rem' }}>Tier 선택</InputLabel>
                                <Select
                                    value={departmentType}
                                    onChange={handleDepartmentTypeChange}
                                    label="Tier 선택"
                                    sx={{ fontSize: '0.78rem' }}
                                >
                                    <MenuItem value="Tier1">Tier1 (최상위 부서)</MenuItem>
                                    <MenuItem value="Tier2">Tier2 (관리 부서)</MenuItem>
                                    <MenuItem value="Tier3">Tier3 (팀 부서)</MenuItem>
                                </Select>
                            </FormControl>

                            {showGeneralDepartmentSelect && (
                                <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel sx={{ fontSize: '0.78rem' }}>상위 부서 (Tier1)</InputLabel>
                                    <Select
                                        value={selectedGeneralDepartment}
                                        onChange={handleGeneralDepartmentChange}
                                        label="상위 부서 (Tier1)"
                                        sx={{ fontSize: '0.78rem' }}
                                    >
                                        {generalDepartments.map(dept => (
                                            <MenuItem key={dept.deptId} value={dept.deptId}>{dept.deptNm}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {showManagementDepartmentSelect && (
                                <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel sx={{ fontSize: '0.78rem' }}>상위 부서 (Tier2)</InputLabel>
                                    <Select
                                        value={selectedManagementDepartment}
                                        onChange={handleManagementDepartmentChange}
                                        label="상위 부서 (Tier2)"
                                        sx={{ fontSize: '0.78rem' }}
                                    >
                                        {managementDepartments.map(dept => (
                                            <MenuItem key={dept.deptId} value={dept.deptId}>{dept.deptNm}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {showNewDepartmentInput && (
                                <>
                                    <TextField
                                        label="신규 부서명"
                                        variant="outlined"
                                        size="small"
                                        autoComplete="off"
                                        value={newDepartment}
                                        onChange={(e) => setNewDepartment(e.target.value)}
                                        InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
                                        inputProps={{ sx: { fontSize: '0.78rem' } }}
                                        sx={{ flex: 1, minWidth: 140 }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddDepartment()}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddDepartment}
                                        disabled={!newDepartment.trim()}
                                        sx={{
                                            flexShrink: 0, borderRadius: '8px', fontWeight: 600,
                                            fontSize: '0.78rem', height: 36, boxShadow: 'none',
                                            backgroundColor: '#1565C0',
                                            '&:hover': { backgroundColor: '#1565C0', boxShadow: 'none' },
                                        }}
                                    >
                                        등록
                                    </Button>
                                </>
                            )}

                            {!showNewDepartmentInput && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                    Tier를 선택하세요  •  노드를 클릭하면 수정/삭제할 수 있습니다
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
                                부서 수정
                            </Typography>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />
                            <TextField
                                label="부서명"
                                variant="outlined"
                                size="small"
                                autoComplete="off"
                                value={editDepartmentName}
                                onChange={(e) => setEditDepartmentName(e.target.value)}
                                InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
                                inputProps={{ sx: { fontSize: '0.78rem' } }}
                                sx={{ flex: 1, minWidth: 160 }}
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateDepartment()}
                            />
                            <Button
                                variant="contained"
                                onClick={handleUpdateDepartment}
                                disabled={!editDepartmentName.trim()}
                                sx={{
                                    flexShrink: 0, borderRadius: '8px', fontWeight: 600,
                                    fontSize: '0.78rem', height: 36, boxShadow: 'none',
                                    backgroundColor: '#1565C0',
                                    '&:hover': { backgroundColor: '#1565C0', boxShadow: 'none' },
                                }}
                            >
                                저장
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDeleteDepartment}
                                sx={{ flexShrink: 0, borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem', height: 36 }}
                            >
                                삭제
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{ flexShrink: 0, borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem', height: 36, borderColor: '#e2e8f0', color: '#64748b' }}
                            >
                                취소
                            </Button>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default OrgManagementDialog;
